import { FlakinessReport } from '@flakiness/flakiness-report';
import { ContextType } from '@lit/context';

import { Multimap } from '@flakiness/shared/common/multimap.js';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { consume, contexts } from '../contexts.js';

import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { WireReport } from '@flakiness/server/common/wireReport.js';
import { CHECK, humanReadableMs } from '@flakiness/shared/common/utils.js';
import { Artifact, artifactFromAttachment, AttachmentUrlBuilder, ClassifiedArtifacts, classifyArtifacts } from '../artifact.js';
import { forgeUtils } from '../forgeUtils.js';
import { Linkifier } from '../linkifier.js';
import { deepQuerySelector, isWebUrl } from '../utils.js';
import { createCodeSnippet } from './code-snippet.js';
import { linkStyles } from './cssstyles.js';
import { isHTMLAttachment } from './html-attachment.js';
import { ImageDiff } from './image-diff-attachment.js';
import { isTextAttachment } from './text-attachment.js';

type TOCEntry = {
  id: string,
  name: string,
  icon: TemplateResult<1>,
};


export type TestRunState = {
  attemptIdx?: number,
}

@customElement('test-run')
class TestRun extends LitElement {

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @property({ attribute: false }) attachmentURLBuilder?: AttachmentUrlBuilder;
  @property({ attribute: false }) wireReport?: WireReport;
  @property({ attribute: false }) attempts?: FlakinessReport.RunAttempt[];
  @property({ attribute: false }) attemptIdx?: number;

  @property({ attribute: false }) linkifier?: Linkifier<TestRunState>;

  @property({ attribute: false }) isRegression?: boolean;

  private _testRunArtifactsTask = new Task(this, {
    args: () => [
      this.attempts, this.attachmentURLBuilder,
    ] as const,
    task: async ([attempts, attachmentURLBuilder], { signal }) => {
      CHECK(attempts && attachmentURLBuilder);

      // Resolve artifact links. Per-attempt classification aggregates attempt-level
      // attachments with every step-level attachment nested under it, so deeply-nested
      // step attachments still surface in the summary sections.
      const attemptToAllArtifacts = new Multimap<FlakinessReport.RunAttempt, Artifact>();
      for (const attempt of attempts) {
        for (const attachment of collectAllAttachments(attempt))
          attemptToAllArtifacts.set(attempt, artifactFromAttachment(attachment, attachmentURLBuilder));
      }

      const attemptToArtifacts = new Map<FlakinessReport.RunAttempt, ClassifiedArtifacts>();
      for (const [attempt, artifacts] of attemptToAllArtifacts)
        attemptToArtifacts.set(attempt, classifyArtifacts(artifacts));


      return { attempts, attemptToArtifacts };
    },
  });

  private _renderError(error: FlakinessReport.ReportError) {
    const value: string = error.stack ? error.stack : error.message ?? error.value ?? '';

    // Ansi snippets are only available for Playwright errors.
    // No other reporters that we're aware of provide them.
    const ansiSnippet = error.snippet;
    if (ansiSnippet) {
      // Position snippet right before stacktrace, if any
      const prefixLines: string[] = value.trim().split('\n'); 
      const suffixLines: string[] = [];
      while (prefixLines.length && /^\s+at\s+/.test(prefixLines.at(-1)!))
        suffixLines.unshift(prefixLines.pop()!);
      // Render ansi snippet
      const newValue = [
        ...prefixLines,
        '',
        ansiSnippet,
        '',
        ...suffixLines
      ].join('\n');
      return html`
        <ansi-text class=error .text=${newValue}></ansi-text>
      `;
    }

    const htmlSnippet = createCodeSnippet(this.wireReport?.report, error.location, 2);
    if (htmlSnippet) {
      return html`
        <ansi-text class=error .text=${value}></ansi-text>
        <code-snippet .snippet=${htmlSnippet} .location=${error.location}></code-snippet>
      `
    }

    return html`
      <ansi-text class=error .text=${value}></ansi-text>
    `
  }

  private _renderAnnotations(annotations: FlakinessReport.Annotation[], tableOfContents: TOCEntry[]) {
    if (!annotations.length)
      return nothing;
    tableOfContents.push({ id: 'section-annotations', name: 'Annotations', icon: html`<sl-icon name=info-circle></sl-icon>` });

    const typeToAnnotations = new Multimap<string, FlakinessReport.Annotation>();
    for (const annotation of annotations)
      typeToAnnotations.set(annotation.type, annotation);
    return html`
      <v-box id=section-annotations>
        ${[...typeToAnnotations].map(([type, annotations]) => html`
          <fk-callout variant=${
            type === 'fixme' || type === 'fail' ? 'danger' :
            type === 'skip' || type === 'slow' ? 'warn' :
            'tip'
          }>
            ${[...annotations].map(annotation => html`
              <h-box style="align-items: start;">
                <strong class=annotation-type>${annotation.type}${annotation.description ? ':' : nothing}</strong>
                <div>
                ${annotation.description && isWebUrl(annotation.description) ? html`
                  <a-ext href=${annotation.description}>${annotation.description}</a-ext>
                ` : annotation.description ? annotation.description : nothing}
                </div>
                ${annotation.location ? html`<x-filler></x-filler><a-location style="color: var(--sl-color-neutral-500);" .project=${this._project} .location=${annotation.location} .commitId=${this.wireReport?.commitId}></a-location>` : nothing}
              </h-box>
            `)}
          </fk-callout>
        `)}
      </v-box>
    `;
  }

  private _renderErrors(errors: FlakinessReport.ReportError[], tableOfContents: TOCEntry[]) {
    if (!errors.length)
      return nothing;
    tableOfContents.push({ id: 'section-errors', name: 'Errors', icon: html`<sl-icon name=bug></sl-icon>` });
    return html`
      <fk-section id="section-errors" open>
        <div slot="title">Errors</div>
        <v-box>
        ${errors.map(error => this._renderError(error))}
        </v-box>
      </fk-section>
    `;
  }

  private _renderSteps(attempt: FlakinessReport.RunAttempt, tableOfContents: TOCEntry[]) {
    tableOfContents.push({ id: 'section-test-steps', name: 'Test Steps', icon: html`<sl-icon name=bar-chart-steps></sl-icon>` });
    const body = attempt.steps && attempt.steps.length ? html` 
      <v-box>
        ${attempt.steps.map(step => html`<test-step .wireReport=${this.wireReport} .step=${step} .commitId=${this.wireReport?.commitId} .attachmentURLBuilder=${this.attachmentURLBuilder}></test-step>`)}
      </v-box>
    ` : html`
      No Steps
    `;
    return html`
      <fk-section open id=section-test-steps>
        <h-box class=steps-title slot="title">
          <span>Test Steps</span>
          <x-filler></x-filler>
          <span>${humanReadableMs(attempt.duration ?? 0)}</span>
        </h-box>
        ${body}
      </fk-section>
    `;
  }

  private _renderImageDiffs(imageDiffs: ImageDiff[], tableOfContents: TOCEntry[]) {
    tableOfContents.push(...imageDiffs.map((imageDiff, index) => ({
      id: `image-diff-${index}`,
      name: imageDiff.name,
      icon: html`<artifact-icon .type=${'image'}></artifact-icon>`
    })));
    return html`
      <div id=section-images></div>
      ${imageDiffs.map((imageDiff, index) => html`
        <fk-section open id=${`image-diff-${index}`}>
          <div slot="title">Image mismatch: ${imageDiff.name}</div>
          <image-diff-attachment .diff=${imageDiff}></image-diff-attachment>
        </fk-section>
      `)}
    `;
  }

  private _renderTraces(traces: Artifact[], tableOfContents: TOCEntry[]) {
    if (!traces.length)
      return nothing;
    tableOfContents.push({ id: 'section-traces', name: 'Traces', icon: html`<artifact-icon .type=${'pw-trace'}></artifact-icon>` });
    return html`
      <fk-section id="section-traces" open>
        <div slot="title">Traces</div>
        <div class=collection-trace-attachments>
          ${traces.map((trace) => html`
            <trace-attachment .artifact=${trace}></trace-attachment>
          `)}
        </div>
      </fk-section>
    `;
  }

  private _renderVideoAttachments(videos: Artifact[], tableOfContents: TOCEntry[]) {
    if (!videos.length)
      return nothing;
    tableOfContents.push({ id: 'section-videos', name: 'Videos', icon: html`<artifact-icon .type=${'video'}></artifact-icon>` });
    return html`
      <fk-section id=section-videos open>
        <div slot="title">Videos</div>
        <div class=collection-video-attachments>
          ${videos.map((video) => html`
            <video-attachment .artifact=${video}></video-attachment>
          `)}
        </div>
      </fk-section>
    `;
  }

  private _renderImageAttachments(images: Artifact[], tableOfContents: TOCEntry[]) {
    if (!images.length)
      return nothing;
    tableOfContents.push({
      id: 'section-images',
      name: 'Images',
      icon: html`<artifact-icon .type=${'image'}></artifact-icon>`
    });
    return html`
      <fk-section id=section-images open>
        <div slot="title">Images</div>
        <div class=collection-image-attachments>
          ${images.map((image) => html`
            <image-attachment .artifact=${image}></image-attachment>
          `)}
        </div>
      </fk-section>
    `;
  }

  // Modern STDIO is after Flakines Report v0.27. This is a single stream of stdio,
  // ordered, with timestamps.
  private _renderModernSTDIO(attempt: FlakinessReport.RunAttempt, tableOfContents: TOCEntry[]) {
    tableOfContents.push({ id: 'section-terminal', name: 'Terminal Output', icon: html`<sl-icon name=terminal></sl-icon>` })
    return html`
      <terminal-stdio id=section-terminal .attempt=${attempt}></terminal-stdio>
    `;
  }

  // Legacy STDIO is pre Flakines Report v0.27. These were separate `stdout` / `stderr` streams without
  // timestamps and/or ordering.
  private _renderLegacySTDIO(attempt: FlakinessReport.RunAttempt, tableOfContents: TOCEntry[]) {
    const decodeSTDIO = (lines?: FlakinessReport.STDIOEntry[]) => lines ? lines.map(line => {
      return ('buffer' in line) ? atob(line.buffer) : line.text;
    }).join('') : '';;

    const stdout = decodeSTDIO(attempt.stdout);
    const stderr = decodeSTDIO(attempt.stderr);
    if (!stdout && !stderr)
      return nothing;
    tableOfContents.push({ id: 'section-terminal', name: 'Terminal Output', icon: html`<sl-icon name=terminal></sl-icon>` })
    return html`
      <fk-section id='section-terminal' open>
        <div slot="title">Terminal Output</div>
        <fk-tabbar selected=${(stdout ? 'stdout' : 'stderr')}>
          <fk-tab name=stdout>stdout</fk-tab>
          <fk-tab name=stderr>stderr</fk-tab>
          <fk-tabpane name=stdout>
            ${stdout ? html`<ansi-text class=legacy-stdio .text=${stdout}></ansi-text>` : html`<span class=no-terminal-output>no output</span>`}
          </fk-tabpane>
          <fk-tabpane name=stderr>
            ${stderr ? html`<ansi-text class=legacy-stdio .text=${stderr}></ansi-text>` : html`<span class=no-terminal-output>no output</span>`}
          </fk-tabpane>
        </fk-tabbar>
      </fk-section>
    `;
  }

  

  private _renderMisc(misc: Artifact[], tableOfContents: TOCEntry[]) {
    if (!misc.length)
      return nothing;
    tableOfContents.push({ id: 'section-attachments', name: 'Attachments', icon: html`<sl-icon name=paperclip></sl-icon>` });
    return html`
      <fk-section id=section-attachments open>
        <div slot="title">Attachments</div>
        <v-box>
          ${misc.map((someArtifact) => isHTMLAttachment(someArtifact) ? html`
            <html-attachment .artifact=${someArtifact}></html-attachment>
          ` : isTextAttachment(someArtifact) ? html`
            <text-attachment .artifact=${someArtifact}></text-attachment>
          ` : html`
            <artifact-link .artifact=${someArtifact}></artifact-link>
          `)}
        </v-box>
      </fk-section>
    `;
  }

  private _renderTOC(attempt: FlakinessReport.RunAttempt, tableOfContents: TOCEntry[]) {
    const report = this.wireReport?.report;
    if (!report)
      return nothing;
    return html`
      <aside>
        <v-box style="gap: var(--sl-spacing-x-small);">
          <div class=subtitle>On this page</div>
          <sl-divider style="--spacing: var(--sl-spacing-2x-small)"></sl-divider>
          ${tableOfContents.map(entry => html`
            <a class=toc-entry href=${'#' + entry.id}>${entry.icon} ${entry.name}</a>
          `)}
        </v-box>
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <div class=subtitle>System Info</div>
          <sl-divider style="--spacing: var(--sl-spacing-2x-small)"></sl-divider>
          ${this._renderSystemData(report, report.environments[attempt.environmentIdx ?? 0])}
        </v-box>
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <div class=subtitle>User Metadata</div>
          <sl-divider style="--spacing: var(--sl-spacing-2x-small)"></sl-divider>
          ${this._renderUserMetadata(report.environments[attempt.environmentIdx ?? 0])}
        </v-box>
      </aside>
    `;
  }

  private _renderSystemData(report: FlakinessReport.Report, env: FlakinessReport.Environment) {
    const os = EnvGroup.osLabel(env.systemData ?? {});

    return html`
      <v-box style="gap: var(--sl-spacing-2x-small);">
        <h-box class=system-field>
          <sl-icon class=system-icon name=tag></sl-icon>
          <span class=system-property>${env.name}</span>
        </h-box>
        ${report.configPath ? html`
          <h-box class=system-field>
            <sl-icon class=system-icon name=folder></sl-icon>
            <a-ext class=system-property href=${forgeUtils.hasSourceUrl(this._project) ? forgeUtils.fileUrl(this._project, report.commitId, report.configPath) : nothing}>${report.configPath}</a-ext>
          </h-box>
        ` : nothing}
        ${os ? html`
          <h-box class=system-field>
            <sl-icon class=system-icon name=cpu></sl-icon>
            <span class=system-property>${os}</span>
          </h-box>
        ` : nothing}
      </v-box>
    `
  }

  private _renderUserMetadata(env: FlakinessReport.Environment) {
    const entries = Object.entries(env.metadata ?? env.userSuppliedData ?? {});
    return html`
      <section class=user-metadata>
        ${entries.map(([key, value]) => html`
          <span class=key>${key}:</span>
          <span class=value>${typeof value === 'object' ? html`<json-viewer-dialog .json=${value}></json-viewer-dialog>` : value}</span>
        `)}
      </section>
    `;
  }

  private _renderAttempt(attempt: FlakinessReport.RunAttempt | undefined, artifacts: ClassifiedArtifacts) {
    if (!attempt)
      return nothing;
    const { imageDiffs, videos, traces, screenshots, misc } = artifacts;
    const tableOfContents: TOCEntry[] = [];
    return html`
      <h-box style="align-items: start; gap: var(--sl-spacing-2x-large);">
        <v-box class=attempt>
          ${this._renderAnnotations(attempt.annotations ?? [], tableOfContents)}
          ${this._renderErrors(attempt.errors ?? [], tableOfContents)}
          ${attempt.stdio ? this._renderModernSTDIO(attempt, tableOfContents) :
            attempt.stderr || attempt.stdout ? this._renderLegacySTDIO(attempt, tableOfContents)
            : nothing
          }
          ${this._renderSteps(attempt, tableOfContents)}
          ${this._renderImageDiffs(imageDiffs, tableOfContents)}
          ${this._renderImageAttachments(screenshots, tableOfContents)}
          ${this._renderTraces(traces, tableOfContents)}
          ${this._renderVideoAttachments(videos, tableOfContents)}
          ${this._renderMisc(misc, tableOfContents)}
        </v-box>
        ${this._renderTOC(attempt, tableOfContents)}
      </h-box>
    `;
  }

  override render() {
    if (!this._testRunArtifactsTask.value)
      return nothing;
    const { attempts, attemptToArtifacts } = this._testRunArtifactsTask.value;

    const selectedAttempt = Math.max(Math.min(this.attemptIdx ?? 0, attempts.length - 1), 0);

    return html`
        <fk-tabbar selected=${selectedAttempt}>
          ${attempts.map((attempt, attemptIdx) => html`
            <fk-tab name=${attemptIdx} .link=${this.linkifier?.({ attemptIdx })}>
              <h-box class=attempt-tab-title>
                <fk-outcome outcome=${
                  attempt.status === 'skipped' ? 'skipped' :
                  attempt.status === attempt.expectedStatus ? 'expected' : 
                  this.isRegression ? 'regressed' : 'unexpected'
                }></fk-outcome>
                ${attemptIdx === 0 ? `Run` : `Retry #${attemptIdx}`}
                ${this._renderArtifactsBlock(attemptToArtifacts.get(attempt))}
                <span style="color: var(--sl-color-neutral-500)">${humanReadableMs(attempt.duration ?? 0)}</span>
              </h-box>
            </fk-tab>
            <fk-tabpane name=${attemptIdx}>
              ${this._renderAttempt(attempt, attemptToArtifacts.get(attempt) ?? classifyArtifacts([]))}
            </fk-tabpane>
          `)}
        </fk-tabbar>
    `;
  }

  private _renderArtifactsBlock(artifacts: ClassifiedArtifacts|undefined) {
    if (!artifacts)
      return nothing;
    const hasImages = artifacts.imageDiffs.length || artifacts.screenshots.length;
    const hasVideos = artifacts.videos.length;
    const hasTraces = artifacts.traces.length;
    if (!hasImages && !hasVideos && !hasTraces)
      return nothing;
    return html`
      <h-box style="gap: var(--sl-spacing-2x-small);">
        ${hasImages ? html`<artifact-icon .type=${"image"}></artifact-icon>` : nothing}
        ${hasVideos ? html`<artifact-icon .type=${"video"}></artifact-icon>` : nothing}
        ${hasTraces ? html`<artifact-icon .type=${"pw-trace"}></artifact-icon>` : nothing}
      </h-box>
    `
  }

  protected updated(_changedProperties: PropertyValues): void {
    if (window.location.hash) {
      const element = deepQuerySelector(window.location.hash,  this.renderRoot);
      element?.scrollIntoView();
    }
  }

  static styles = [linkStyles, css`
    .annotation-type {
      white-space: nowrap;
    }
    aside {
      position: sticky;
      margin-top: var(--sl-spacing-medium);
      top: var(--sl-spacing-medium);
      width: 250px;
      display: flex;
      flex-direction: column;
      flex: none;
      gap: var(--sl-spacing-large);

      .subtitle {
        font-weight: var(--sl-font-weight-bold);
        font-size: var(--sl-font-size-small);
      }

      .toc-entry {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-x-small);
      }
    }

    ansi-text.error, ansi-text.legacy-stdio {
      padding: var(--sl-spacing-medium);
      background-color: var(--sl-color-neutral-50);
    }

    .attempt {
      flex: auto;
      /* overflow: auto; */
    }

    .attempt-tab-title {
      white-space: nowrap;
    }
    .collection-image-attachments {
      margin: var(--sl-spacing-large);
      display: flex;
      flex-wrap: wrap;
      gap: var(--sl-spacing-large);
    }

    .collection-video-attachments {
      margin: var(--sl-spacing-large);
      display: flex;
      flex-wrap: wrap;
      gap: var(--sl-spacing-large);
    }

    .dropshadow {
      box-shadow: 0 0 5px var(--sl-color-neutral-400);
    }

    .collection-trace-attachments {
      margin: var(--sl-spacing-large);
      display: flex;
      flex-wrap: wrap;
      gap: var(--sl-spacing-large);
    }

    .steps-title {
      flex: auto;
    }

    .no-terminal-output {
      color: var(--sl-color-neutral-400);
    }

    .user-metadata {
      display: grid;
      grid-template-columns: auto 1fr;
      column-gap: var(--sl-spacing-2x-small);
      row-gap: var(--sl-spacing-x-small);

      .key {
        white-space: nowrap;
        max-width: 125px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .value {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .system-field {
      gap: var(--sl-spacing-x-small);
      white-space: nowrap;

      .system-icon {
        flex: none;
      }
    }
  `];
}

function collectAllAttachments(attempt: FlakinessReport.RunAttempt): FlakinessReport.Attachment[] {
  const result = new Map<string,FlakinessReport.Attachment>();

  const add = (attachment: FlakinessReport.Attachment) => {
    result.set(`${attachment.id}:${attachment.name}`, attachment);
  };
  for (const attachment of attempt.attachments ?? [])
    add(attachment);
  const walk = (steps: FlakinessReport.TestStep[] | undefined) => {
    for (const step of steps ?? []) {
      for (const attachment of step.attachments ?? [])
        add(attachment);
      walk(step.steps);
    }
  };
  walk(attempt.steps);
  return Array.from(result.values());
}
